import React from 'react';
import SEO from '@/components/SEO';

// DRAFT privacy policy describing what this website actually does with personal data.
// It has NOT been reviewed by a lawyer. Have it checked (e.g. against India's DPDP Act 2023)
// before relying on it, and update it whenever the site starts collecting new data.
const LAST_UPDATED = '11 October 2026';
const CONTACT_EMAIL = 'info@gangadharsom.art';

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="font-serif text-xl sm:text-2xl mb-3">{title}</h2>
    <div className="font-sans text-sm sm:text-base text-muted-foreground leading-relaxed space-y-3">{children}</div>
  </section>
);

const Privacy = () => (
  <div className="min-h-screen pt-28 pb-20 px-4 sm:px-6" data-testid="privacy-page">
    <SEO title="Privacy Policy | Gangadhar Som" description="How gangadharsom.art collects and uses personal information." />
    <div className="max-w-3xl mx-auto">
      <h1 className="font-serif text-3xl sm:text-4xl mb-2">Privacy Policy</h1>
      <p className="font-sans text-xs text-muted-foreground mb-10">Last updated: {LAST_UPDATED}</p>

      <Section title="Who we are">
        <p>This website, gangadharsom.art, is the official site of artist Gangadhar Som. It shows his artwork and lets
          visitors buy originals, prints and digital downloads, and request commissions. Questions about this policy:
          {' '}<a className="text-accent underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</p>
      </Section>

      <Section title="What we collect">
        <p><strong>When you place an order:</strong> your name, email address, phone number and shipping address, and
          details of what you ordered. Payments are handled by Razorpay; we never see or store your card or bank details.</p>
        <p><strong>When you sign in with Google:</strong> your name, email address and profile picture from your Google
          account. We use these only to log you in and show your profile, wishlist and orders.</p>
        <p><strong>When you send a commission request, contact message or review:</strong> the information you type in,
          and any reference images you upload.</p>
        <p><strong>Newsletter:</strong> your email address, if you subscribe.</p>
        <p><strong>Visit statistics:</strong> which pages are viewed, so we can see what visitors are interested in. If
          Google Analytics, Meta Pixel or Pinterest Tag is switched on in our settings, those services also receive
          information about your visit under their own privacy policies.</p>
      </Section>

      <Section title="How we use it">
        <p>To process and deliver your order, send order confirmations and shipping updates, reply to your messages and
          commission requests, and run the website. If you leave items in your cart while signed in, we may send one
          reminder email. We do not sell your personal information.</p>
      </Section>

      <Section title="Who we share it with">
        <p>Only the services needed to run the site and fulfil orders:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Razorpay: payments</li>
          <li>Qikink (India) and Prodigi (international): printing and shipping print orders, which receive your name, address and phone number</li>
          <li>Brevo: sending emails</li>
          <li>Google: sign-in, and AI tools used to describe artwork images (no customer data is sent to these)</li>
          <li>Cloudflare, Google Cloud and MongoDB Atlas: hosting, image storage and our database</li>
        </ul>
      </Section>

      <Section title="How long we keep it">
        <p>Order records are kept as long as needed for accounting, tax and customer service. You can ask us to delete
          your account or other information at any time, unless we must keep it by law.</p>
      </Section>

      <Section title="Your choices">
        <p>You can ask us for a copy of your information, ask us to correct or delete it, or unsubscribe from emails, by
          writing to <a className="text-accent underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</p>
      </Section>

      <Section title="Changes">
        <p>If we change this policy, we will update the date at the top of this page.</p>
      </Section>
    </div>
  </div>
);

export default Privacy;
